<template>
  <div>
    <club-player-tabs />

    <!-- Фильтр по датам -->
    <v-card class="filters mb-4 pa-4">
      <v-row align="center">
        <v-col cols="12" sm="6" md="3">
          <v-menu
            v-model="menuFrom"
            attach
            offset-y
            :close-on-content-click="false"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="dateFrom"
                label="Дата от"
                readonly
                clearable
                dense
                outlined
                hide-details
                v-bind="attrs"
                v-on="on"
                @click:clear="onDateFromClear"
              />
            </template>
            <v-date-picker
              v-model="dateFrom"
              no-title
              @input="menuFrom = false"
            />
          </v-menu>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-menu
            v-model="menuTo"
            attach
            offset-y
            :close-on-content-click="false"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="dateTo"
                label="Дата до"
                readonly
                clearable
                dense
                outlined
                hide-details
                v-bind="attrs"
                v-on="on"
                @click:clear="onDateToClear"
              />
            </template>
            <v-date-picker
              v-model="dateTo"
              no-title
              @input="menuTo = false"
            />
          </v-menu>
        </v-col>

        <v-col cols="12" sm="auto">
          <v-btn
            color="primary"
            depressed
            small
            @click="applyFilters"
          >
            Применить
          </v-btn>
        </v-col>

        <v-col cols="12" sm="auto">
          <v-btn
            color="error"
            depressed
            small
            @click="clearDateFilters"
          >
            Сбросить даты
          </v-btn>
        </v-col>
      </v-row>

      <v-row
        align="center"
        class="mt-1"
      >
        <v-col cols="12" sm="auto">
          <v-btn
            depressed
            small
            class="filter-btn-accent white--text"
            @click="applyPresetPrevQuarter"
          >
            Прошлый квартал
          </v-btn>
        </v-col>

        <v-col cols="12" sm="auto">
          <v-btn
            depressed
            small
            class="filter-btn-accent white--text"
            @click="applyPresetCurrentQuarter"
          >
            Текущий квартал
          </v-btn>
        </v-col>

        <v-col cols="12" sm="auto">
          <v-btn
            depressed
            small
            class="filter-btn-accent white--text"
            @click="applyPresetCurrentYear"
          >
            Текущий год
          </v-btn>
        </v-col>
      </v-row>

      <!-- Фильтр по клиенту -->
      <v-row
        align="center"
        class="mt-2"
      >
        <v-col
          cols="12"
          md="6"
        >
          <autocomplete
            ref="clientAutocomplete"
            @updateParent="onClientSelected"
          />
        </v-col>
        <v-col
          v-if="selectedClient"
          cols="12"
          sm="auto"
        >
          <v-btn
            color="error"
            depressed
            small
            @click="clearClientFilter"
          >
            Сбросить клиента
          </v-btn>
        </v-col>
      </v-row>

      <div
        v-if="periodInfo"
        class="mt-3 text--secondary text-body-2"
      >
        Период: <strong>{{ periodInfo.label }}</strong>
        <span v-if="periodInfo.is_default"> (прошлый квартал, по умолчанию)</span>
      </div>
      <v-alert
        v-if="periodDateError"
        type="warning"
        text
        dense
        class="mt-2 mb-0"
      >
        {{ periodDateError }}
      </v-alert>
    </v-card>

    <!-- Статистика по выбранному клиенту (loyalty + history) -->
    <v-card
      v-if="selectedClient"
      class="mb-4 pa-4"
    >
      <club-player-client-stats-panel
        :user-id="selectedClient.id"
        :user-name="selectedClient.name"
        :fallback-loyalty-status="selectedClient.loyalty_status"
        :date-from="dateFrom"
        :date-to="dateTo"
        external-period
        :show-period-statistics="false"
      />
    </v-card>

    <!-- Агрегированная статистика (с user_id при выборе клиента) -->
    <club-player-period-statistics
      ref="periodStatistics"
      :user-id="selectedClient?.id ?? null"
      :date-from="dateFrom"
      :date-to="dateTo"
      :single-client="!!selectedClient"
      show-empty-hint
      @loaded="onPeriodStatsLoaded"
    />
  </div>
</template>

<script>
import api from '@/api/api'
import Autocomplete from '@/components/Autocomplete.vue'
import ClubPlayerTabs from '@/views/club_player/ClubPlayerTabs.vue'
import ClubPlayerClientStatsPanel from '@/views/club_player/ClubPlayerClientStatsPanel.vue'
import ClubPlayerPeriodStatistics from '@/views/club_player/ClubPlayerPeriodStatistics.vue'
import {
  extractLoyaltyStatus,
  getQuarterDates,
} from '@/views/club_player/loyaltyConstants'

export default {
  name: 'ClubPlayerStatistics',
  components: {
    ClubPlayerTabs,
    Autocomplete,
    ClubPlayerClientStatsPanel,
    ClubPlayerPeriodStatistics,
  },

  data() {
    return {
      dateFrom: null,
      dateTo: null,
      menuFrom: false,
      menuTo: false,
      selectedClient: null,
      periodInfo: null,
      periodDateError: null,
      _skipRouteWatch: false,
    }
  },

  watch: {
    '$route.query': {
      handler() {
        if (this._skipRouteWatch) return
        this.readFiltersFromRoute()
      },
      deep: true,
    },
    dateFrom() {
      this.validatePeriodDates()
    },
    dateTo() {
      this.validatePeriodDates()
    },
  },

  mounted() {
    this.readFiltersFromRoute()
  },

  methods: {
    readFiltersFromRoute() {
      const q = this.$route.query
      this.dateFrom = q.from || null
      this.dateTo = q.to || null
      const userId = q.user_id
      if (userId) {
        const id = Number(userId)
        const name = q.user_name ? String(q.user_name) : `ID ${userId}`
        this.selectedClient = Number.isFinite(id) ? { id, name } : null
        if (this.selectedClient) {
          this.enrichSelectedClientStatus(this.selectedClient.id)
        }
      } else {
        this.selectedClient = null
      }
    },

    writeFiltersToRoute() {
      const query = {}
      if (this.dateFrom) query.from = this.dateFrom
      if (this.dateTo) query.to = this.dateTo
      if (this.selectedClient?.id != null) {
        query.user_id = String(this.selectedClient.id)
        if (this.selectedClient.name) query.user_name = this.selectedClient.name
      }

      const cur = this.$route.query
      const same = cur.from === (query.from || undefined)
        && cur.to === (query.to || undefined)
        && String(cur.user_id || '') === String(query.user_id || '')
        && String(cur.user_name || '') === String(query.user_name || '')
      if (same) return

      this._skipRouteWatch = true
      this.$router.replace({ path: this.$route.path, query })
        .catch(() => {})
        .finally(() => {
          this.$nextTick(() => { this._skipRouteWatch = false })
        })
    },

    onClientSelected(client) {
      if (!client?.id) return
      this.selectedClient = {
        id: client.id,
        name: client.name || '',
        loyalty_status: client.loyalty_status || null,
      }
      this.writeFiltersToRoute()
      this.enrichSelectedClientStatus(client.id)
    },

    async enrichSelectedClientStatus(userId) {
      if (userId == null || !this.selectedClient || this.selectedClient.id !== userId) {
        return
      }
      let status = this.selectedClient.loyalty_status || null
      try {
        const data = await api.getClubPlayerLoyaltyStatus({ user_id: userId })
        status = extractLoyaltyStatus(data, status)
      } catch (e) {
        console.error('enrichSelectedClientStatus: loyalty_status', e)
      }
      if (!status && this.selectedClient.name) {
        try {
          const data = await api.getClubPlayerClients({
            params: {
              name: this.selectedClient.name,
              page: 1,
              page_size: 50,
            },
          })
          const row = (data?.results || []).find(
            r => Number(r.user_id) === Number(userId) || Number(r.id) === Number(userId),
          )
          if (row?.loyalty_status) {
            status = row.loyalty_status
          }
        } catch (e) {
          console.error('enrichSelectedClientStatus: users_points', e)
        }
      }
      if (status && this.selectedClient?.id === userId) {
        this.$set(this.selectedClient, 'loyalty_status', status)
      }
    },

    clearClientFilter() {
      this.selectedClient = null
      if (this.$refs.clientAutocomplete?.clearSelection) {
        this.$refs.clientAutocomplete.clearSelection()
      }
      this.writeFiltersToRoute()
    },

    clearDateFilters() {
      this.dateFrom = null
      this.dateTo = null
      this.periodDateError = null
      this.applyFilters()
    },

    validatePeriodDates() {
      const hasFrom = !!this.dateFrom
      const hasTo = !!this.dateTo
      if (hasFrom !== hasTo) {
        this.periodDateError = 'Укажите обе даты периода: «Дата от» и «Дата до».'
        return false
      }
      this.periodDateError = null
      return true
    },

    applyFilters() {
      if (!this.validatePeriodDates()) return
      this.writeFiltersToRoute()
      this.$refs.periodStatistics?.fetchStatistics()
    },

    applyPresetPrevQuarter() {
      const { from, to } = getQuarterDates(-1)
      this.dateFrom = from
      this.dateTo = to
      this.periodDateError = null
      this.applyFilters()
    },

    applyPresetCurrentQuarter() {
      const { from, to } = getQuarterDates(0)
      this.dateFrom = from
      this.dateTo = to
      this.periodDateError = null
      this.applyFilters()
    },

    applyPresetCurrentYear() {
      const year = new Date().getFullYear()
      this.dateFrom = `${year}-01-01`
      this.dateTo = `${year}-12-31`
      this.periodDateError = null
      this.applyFilters()
    },

    onDateFromClear() {
      this.dateFrom = null
      if (this.dateTo) {
        this.periodDateError = 'Укажите обе даты периода: «Дата от» и «Дата до».'
      } else {
        this.periodDateError = null
      }
    },

    onDateToClear() {
      this.dateTo = null
      if (this.dateFrom) {
        this.periodDateError = 'Укажите обе даты периода: «Дата от» и «Дата до».'
      } else {
        this.periodDateError = null
      }
    },

    onPeriodStatsLoaded(stats) {
      if (stats?.period) {
        this.periodInfo = stats.period
      }
    },
  },
}
</script>

<style scoped>
.filter-btn-accent {
  background-color: #14b1ff !important;
}
</style>
